import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Check, UploadCloud } from "lucide-react";
import {
  editScenario,
  editScenarioImage,
  getCategories,
  getDetails,
} from "../../requests.js";
import {
  startLoader,
  stopLoader,
} from "../../_store/reducers/loaderReducer.js";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper.js";
import { useDispatch } from "react-redux";
import styles from "../../_css/RoleplayManagement.module.css";
import SelectComponent from "../common/SelectComponent.js";
import _ from "lodash";
import { COMPETENCIES_BY_LANGUAGE } from "../../data/competencyData.js";

const EditScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // 1. Capture the return state sent from RoleManager
  const returnState = {
    categoryId: location.state?.categoryId,
    page: location.state?.page,
    searchTerm: location.state?.searchTerm,
    selectedType: location.state?.selectedType
  };

  const scenarioId = location.state?.scenarioData?.id || location.state?.scenarioId;

  const [selectedStyle, setSelectedStyle] = useState([]);
  const [hoveredStyle, setHoveredStyle] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [scenerioImage, setScenerioImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCompetenciesList, setCurrentCompetenciesList] = useState(COMPETENCIES_BY_LANGUAGE.English);

  const wordLimits = {
    subCategory: 40,
    scenarioTitle: 40,
    scenarioShortDescription: 50,
    scenarioDescription: 100,
    goals: 100,
  };

  const [formData, setFormData] = useState({
    subCategory: "",
    scenarioTitle: "",
    scenarioDescription: "",
    scenarioShortDescription: "",
    goals: "",
  });

  // Helper to format Label(lang) for display
  const formatCategoryDisplay = (label, language) => {
    if (!label) return "";
    const langCode = language?.toLowerCase() === "english" ? "en" :
      language?.toLowerCase() === "dutch" ? "nl" :
        language?.toLowerCase() === "french" ? "fr" :
          language?.substring(0, 2).toLowerCase();
    return langCode ? `${label}(${langCode})` : label;
  };

  useEffect(() => {
    fetchCategories();
    fetchScenarioData();
  }, [scenarioId]);

  const countWords = (text) => {
    if (!text || typeof text !== "string") return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };
  const getWordCountStyle = (text, limit) => {
    return countWords(text) > limit ? { color: "red", fontWeight: "bold" } : {};
  };

  const handleInputChange = (fieldName, value) => {
    const limit = wordLimits[fieldName];
    const words = value.trim().split(/\s+/).filter(Boolean);
    const currentWordCount = countWords(value);
    if (currentWordCount > limit) {
      notifyError(`Cannot add more than ${limit} words for this field.`);
      const truncatedValue = value
        .split(/\s+/)
        .slice(0, limit)
        .join(" ");
      setFormData((prev) => ({ ...prev, [fieldName]: truncatedValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      // Map categories to include formatted labels for the dropdown list
      const formattedCats = (response.categories || []).map(cat => ({
        ...cat,
        displayLabel: formatCategoryDisplay(cat.label, cat.language)
      }));
      setCategories(formattedCats);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchScenarioData = async () => {
    if (!scenarioId) return;
    dispatch(startLoader());
    try {
      const response = await getDetails(scenarioId);
      if (response.success) {
        const data = response.data;
        // Set the scenario's specific category ID
        setCategory(data?.category);

        const scenarioLang = data.language || "English";
        const activeList = COMPETENCIES_BY_LANGUAGE[scenarioLang] || COMPETENCIES_BY_LANGUAGE.English;
        setCurrentCompetenciesList(activeList);

        setFormData({
          subCategory: data.title || "",
          scenarioTitle: data.short_description || "",
          scenarioDescription: data.scenario || "",
          scenarioShortDescription: data.summary || "",
          goals: Array.isArray(data.learning_goals) ? data.learning_goals.join("\n") : data.learning_goals || "",
        });

        const apiCompetencies = (data.competencies || []).map(c => c.split("–")[0].trim());
        const matchedCompetencies = activeList.filter((comp) =>
          apiCompetencies.some((apiComp) => apiComp.toLowerCase() === comp.toLowerCase())
        );

        setSelectedStyle(matchedCompetencies);
        setUploadedImage(data?.image);
      }
    } catch (err) {
      console.error(err);
    }
    dispatch(stopLoader());
  };

  const handleSave = async () => {
    if (!formData.scenarioTitle.trim() || !category || !selectedStyle.length) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    dispatch(startLoader());
    try {
      const payload = {
        id: scenarioId,
        title: formData.scenarioTitle,
        short_description: formData.scenarioShortDescription,
        description: formData.scenarioDescription,
        goals: formData.goals,
        competencies: selectedStyle,
        category: category,
        subCategory: formData.subCategory,
      };

      const response = await editScenario(payload);
      if (scenerioImage) {
        await editScenarioImage(scenerioImage, scenarioId);
      }

      if (response.success) {
        notifySuccess("Roleplay Edited Successfully!");
        navigate("/roleplayManagement", { state: returnState });
      } else {
        notifyError("Failed to update scenario");
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.href = "/login";
      }
      notifyError(error?.response?.data?.message || "Failed to update scenario");
    } finally {
      setIsLoading(false);
      dispatch(stopLoader());
    }
  };

  // Find exact matching category option for the SelectComponent value
  const getCategoryOption = () => {
    if (!category || !categories.length) return null;
    const found = categories.find((option) => _.toString(option.value) === _.toString(category));
    if (found) {
      return { ...found, label: found.displayLabel }; // Use formatted label for display
    }
    return null;
  };

  const handleStyleClick = (style) => {
    const normalizedStyle = style.trim();
    if (selectedStyle.includes(normalizedStyle)) {
      setSelectedStyle(selectedStyle.filter((item) => item !== normalizedStyle));
    } else if (selectedStyle.length < 3) {
      setSelectedStyle([...selectedStyle, normalizedStyle]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setScenerioImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setUploadedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const chunkCompetencies = () => {
    const chunkSize = 7;
    const chunks = [];
    for (let i = 0; i < currentCompetenciesList.length; i += chunkSize) {
      chunks.push(currentCompetenciesList.slice(i, i + chunkSize));
    }
    return chunks;
  };

  return (
    <div className={styles.editScenarioContainer}>
      <div className={styles.editScenarioHeader}>
        <button
          onClick={() => navigate("/roleplayManagement", { state: returnState })}
          className={styles.editScenarioBackButton}
        >
          <ArrowLeft size={20} color="#171717" />
        </button>
        <h1 className={`font-clash-display ${styles.editScenarioTitle}`}>Edit Scenario</h1>
      </div>

      <div className={styles.editScenarioFormContainer} style={{ flexDirection: uploadedImage ? "row" : "column" }}>
        <div className={`${styles.editScenarioFormContent} ${uploadedImage ? styles.editScenarioFormContentWithImage : styles.editScenarioFormContentFull}`}>
          <div className={styles.editScenarioSectionTitle}>
            <h1 className={styles.editScenarioSectionHeading}>Edit Scenario Content</h1>
            <span className={styles.editScenarioSectionSubtitle}>Edit Scenario title, description, image</span>
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <label className={styles.editScenarioLabel}>📝 Category</label>
            <SelectComponent
              optionsData={categories.map(c => ({ ...c, label: c.displayLabel }))}
              placeholder="Select Category"
              handleValueChange={(opt) => setCategory(opt?.value || null)}
              value={getCategoryOption()}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Sub Category</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.subCategory)}/{wordLimits.subCategory}</span>
            </div>
            <textarea
              className={`form-control custom-placeholder ${styles.editScenarioTextarea}`}
              value={formData.subCategory}
              onChange={(e) => handleInputChange("subCategory", e.target.value)}
              rows={1}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Scenario Title</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.scenarioTitle)}/{wordLimits.scenarioTitle}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioTitle}
              onChange={(e) => handleInputChange("scenarioTitle", e.target.value)}
              rows={1}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Short Description</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.scenarioShortDescription)}/{wordLimits.scenarioShortDescription}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioShortDescription}
              onChange={(e) => handleInputChange("scenarioShortDescription", e.target.value)}
              rows={3}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-2 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>📝 Description</label>
              <span className={styles.editScenarioWordCount} style={getWordCountStyle(formData.scenarioDescription, wordLimits.scenarioDescription)}>Words: {countWords(formData.scenarioDescription)}/{wordLimits.scenarioDescription}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.scenarioDescription}
              onChange={(e) => handleInputChange("scenarioDescription", e.target.value)}
              rows={3}
            />
          </div>

          <div className={`font-plus-jakarta-sans mb-4 ${styles.editScenarioFieldWrapper}`}>
            <div className={styles.editScenarioFieldHeader}>
              <label className={styles.editScenarioLabel}>🎯 Key Learning Goals</label>
              <span className={styles.editScenarioWordCount}>Words: {countWords(formData.goals)}/{wordLimits.goals}</span>
            </div>
            <textarea
              className={`form-control custome-placeholder ${styles.editScenarioTextarea}`}
              value={formData.goals}
              onChange={(e) => handleInputChange("goals", e.target.value)}
              rows={3}
            />
          </div>

          {!uploadedImage && (
            <div className={styles.editScenarioUploadArea}>
              <div className={styles.editScenarioUploadBox}>
                <UploadCloud size={32} color="#000000" />
                <label htmlFor="file-upload-0" className={styles.editScenarioUploadLabel}>Upload an image</label>
                <input id="file-upload-0" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
                <p className={styles.editScenarioUploadHint}>PNG or Jpeg only</p>
              </div>
            </div>
          )}
        </div>

        {uploadedImage && (
          <div className={styles.editScenarioImagePreview}>
            <div className={styles.editScenarioImageContainer}>
              <img
                src={uploadedImage.startsWith("data:") || uploadedImage.includes("http") ? uploadedImage : `${process.env.PUBLIC_URL}/img/${uploadedImage}.png`}
                alt="Scenario"
                className={styles.editScenarioImage}
              />
              <button onClick={() => setUploadedImage(null)} className={styles.editScenarioChangeImageButton}>Change Image</button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.editScenarioCompetencySection}>
        <div className={styles.editScenarioCompetencyHeader}>
          <h1 className={`font-clash-display ${styles.editScenarioCompetencyTitle}`}>Linked Competency</h1>
          <span className={styles.editScenarioCompetencySubtitle}>Link competency to the Scenario</span>
        </div>
        <div className={styles.editScenarioCompetencyList}>
          {chunkCompetencies().map((row, rowIndex) => (
            <div key={rowIndex} className={styles.editScenarioCompetencyRow}>
              {row.map((style) => (
                <button
                  className={`${styles.editScenarioCompetencyButton} ${selectedStyle.includes(style) ? styles.editScenarioCompetencyButtonSelected : ""}`}
                  key={style}
                  onClick={() => handleStyleClick(style)}
                  onMouseEnter={() => setHoveredStyle(style)}
                  onMouseLeave={() => setHoveredStyle(null)}
                  style={{ color: selectedStyle.includes(style) || hoveredStyle === style ? "#23B4C3" : "#6B7280" }}
                >
                  {selectedStyle.includes(style) && <Check size={16} />}
                  {style}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.editScenarioActionButtons}>
        <button className="next-button" style={{ color: "#FFFFFF", border: "none", backgroundColor: "#23B4C3", borderRadius: '21px', fontWeight: 500, fontSize: "16px" }} onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </button>
        <button className="back-button" style={{ border: "none" }} onClick={() => navigate("/scenarioPreview", { state: { from: "editScenario", returnState, scenarioData: { ...formData, id: scenarioId, competencies: selectedStyle, image: uploadedImage } } })}>
          Preview Scenario
        </button>
      </div>
    </div>
  );
};

export default EditScenario;